/**
 * HelpWithVideoTour — drop-in wrapper for help modal bodies.
 * Adds Guide | Video Tour tabs when a tour exists for guideId.
 */

import React, { useState } from 'react';
import HelpVideoTour from './HelpVideoTour';
import { getHelpTour } from './helpTours/registry';
import { TT_FIELD_INTERACTIVE_STEPS, TT_ADMIN_INTERACTIVE_STEPS } from '../Actuals/timeTrackerInteractiveSteps';

const FONT = '"Segoe UI", system-ui, -apple-system, sans-serif';

export default function HelpWithVideoTour({
  guideId,
  title,
  accent = '#0369a1',
  defaultTab = 'guide',
  /** Negative top/side margin to bleed tabs to modal edges (CbUi uses 24). */
  bleed = 0,
  /** 'field' | 'admin' | null — adds Interactive Tour tab that spotlights the live page */
  interactiveVariant = null,
  onStartInteractive,
  extraTabs = [],
  children,
}) {
  const tour = getHelpTour(guideId, { title, accent });
  const [tab, setTab] = useState(defaultTab === 'tour' && tour ? 'tour' : 'guide');

  if (!tour) {
    return <>{children}</>;
  }

  const interactiveSteps = interactiveVariant === 'admin'
    ? TT_ADMIN_INTERACTIVE_STEPS
    : interactiveVariant === 'field'
      ? TT_FIELD_INTERACTIVE_STEPS
      : null;

  const tabs = [
    { id: 'guide', label: 'Guide' },
    { id: 'tour', label: 'Video Tour' },
    ...(interactiveSteps ? [{ id: 'interactive', label: 'Interactive Tour' }] : []),
    ...extraTabs.map((t) => ({ id: t.id, label: t.label })),
  ];

  const bleedMargin = bleed
    ? { marginTop: -bleed, marginLeft: -bleed, marginRight: -bleed }
    : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, fontFamily: FONT }}>
      {/* Tab bar — flexShrink + zIndex keep tabs above scrollable body */}
      <div style={{
        display: 'flex', flexShrink: 0, position: 'relative', zIndex: 3,
        borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa',
        ...bleedMargin,
      }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              padding: '12px 18px', border: 'none', flexShrink: 0,
              borderBottom: tab === t.id ? `3px solid ${accent}` : '3px solid transparent',
              backgroundColor: tab === t.id ? 'white' : 'transparent',
              color: tab === t.id ? accent : '#6b7280',
              fontWeight: tab === t.id ? 600 : 400,
              cursor: 'pointer', fontSize: 14, whiteSpace: 'nowrap',
              position: 'relative', zIndex: 1, fontFamily: FONT,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{
        paddingTop: 16,
        flex: 1, minHeight: 0,
        overflowY: tab === 'guide' ? 'visible' : 'auto',
      }}>
        {tab === 'guide' && children}
        {tab === 'tour' && (
          <HelpVideoTour
            guideId={tour.guideId}
            title={tour.title}
            accent={tour.accent}
            scenes={tour.scenes}
            compact
            showHeader
          />
        )}
        {tab === 'interactive' && interactiveSteps && (
          <div style={{ padding: '8px 4px 16px' }}>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
              The video tour is illustrated. This one walks the <strong>real buttons</strong> on the page
              behind this window. We will close Help and highlight each control.
            </p>
            <ol style={{ margin: '0 0 16px', paddingLeft: 20, fontSize: 13, color: '#475569', lineHeight: 1.55 }}>
              {interactiveSteps.map((s) => (
                <li key={s.id} style={{ marginBottom: 6 }}>
                  <strong style={{ color: '#0f172a' }}>{s.title}.</strong> {s.content}
                </li>
              ))}
            </ol>
            <button
              type="button"
              onClick={() => {
                if (onStartInteractive) onStartInteractive(interactiveVariant);
                else window.dispatchEvent(new CustomEvent('tt:startInteractiveTour', { detail: { variant: interactiveVariant } }));
              }}
              style={{
                padding: '10px 16px', border: 'none', borderRadius: 8, cursor: 'pointer',
                background: accent, color: '#fff', fontWeight: 700, fontSize: 13,
              }}
            >
              Start interactive tour
            </button>
          </div>
        )}
        {extraTabs.map((t) => (
          tab === t.id ? <div key={t.id}>{t.content}</div> : null
        ))}
      </div>
    </div>
  );
}

/** Whether a guideId has a video tour (override or auto from narration). */
export function hasHelpTour(guideId) {
  return !!getHelpTour(guideId);
}
